// PO-creation preconditions — the pure, DB-free mirror of the server's
// PoPrereqError checks (services/purchase-orders-builder.ts::derivePosFromOrder)
// plus the zero-cost free-goods guard.
//
// Shared on purpose (same discipline as isSoSignoffReachable / order-money):
//   - the CLIENT disables the Create-POs affordance with an HONEST reason
//     BEFORE a round-trip (no "click → 409 → guess what's wrong" ping-pong);
//   - the SERVER keeps the authoritative backstop (derivePosFromOrder throws
//     PoPrereqError, and the $0 guard throws 409 zero_cost) — a free-goods PO
//     must never mint silently;
//   - the offline QA gate EXECUTES the SAME decision against fixtures, so what
//     the button shows and what the endpoint enforces can never drift.
//
// DB-free: the caller normalises whatever it holds (the web order payload / the
// server's loaded line rows) into the small input shapes below.

/** Why the Create-POs action is blocked — mirrors the server's PoPrereqError
 *  reasons (vendor_missing is the client-facing name for vendor_lookup_failed). */
export type PoBlockReason =
  | "no_shipping_address"
  | "no_line_items"
  | "vendor_missing"
  | "missing_artwork"
  | "missing_sizes";

/** One decoration LOCATION on a precondition line — the per-location artwork
 *  gate reads these so the client runs the SAME decision as the server. */
export interface PoPreconditionDecoration {
  /** The decoration's location label (drives the (item, location) pair). */
  location: string | null;
  /** logo_file_id is set (the uploaded artwork). */
  hasLogoFile: boolean;
  /** logo_name is set (non-blank). */
  hasLogoName: boolean;
  /** proof_type is chosen (any enum value, incl. 'none'). */
  hasProofType: boolean;
}

/** The per-line slice the precondition deriver reads. Product lines only —
 *  service lines (shipping/setup) never get POs, so the caller filters first. */
export interface PoPreconditionLine {
  /** The order line id — surfaced back as offendingLineId so the UI can deep-
   *  link the reason to the exact line that needs fixing. */
  id: string;
  /** Human item name (productSnapshot.name) — surfaced in the (item, location)
   *  pairs the per-location artwork gate names. */
  itemName: string;
  /** productSnapshot.vendorId resolved — every PO'd line needs a supplier. */
  hasVendor: boolean;
  /** decorationPath is a real decoration (not blank / null) → the per-location
   *  artwork gate applies to this line. */
  needsArtwork: boolean;
  /** The line's decoration locations (empty when the line has none yet — a
   *  decorated line with no locations is itself a gap). */
  decorations: PoPreconditionDecoration[];
  /** The product comes in colors/sizes but the line has no breakdown yet
   *  (GET /orders/:id/variant-options needsBreakdown + no variants). */
  missingSizes?: boolean;
}

export interface PoPreconditionInput {
  /** order.shippingAddressId is set — the EXACT signal derivePosFromOrder reads
   *  (not the snapshot, which can linger as a placeholder). */
  hasShippingAddress: boolean;
  /** Product-type lines only (service lines don't get POs). */
  productLines: PoPreconditionLine[];
  /** The quote's "Blank order" box (Stephanie 2026-10-14). false = unticked:
   *  the logo is REQUIRED (no "Create anyway"). null/undefined = the box isn't
   *  available yet (DB column not added) — the old behaviour. Callers also use
   *  lineNeedsArtwork() so a ticked box needs no artwork at all. */
  blankOrder?: boolean | null;
}

/** Does this line need its logo before Shipping / Create POs?
 *  Blank order ticked → never. A line marked blank (no decoration) → never.
 *  Unticked → every other line, including one with no decoration method set
 *  yet (Stephanie 2026-10-14: "If this box is not checked, a logo is
 *  required"). Box not available yet (null) → only lines with a decoration
 *  method, as before. */
export function lineNeedsArtwork(
  decorationPath: string | null | undefined,
  blankOrder: boolean | null | undefined,
): boolean {
  if (blankOrder === true) return false;
  if (decorationPath === "blank_no_decoration") return false;
  if (decorationPath == null) return blankOrder === false;
  return true;
}

/** The (item, location) pairs still missing their logo — the same per-location
 *  rule as Create POs. Used to lock the Shipping tab (only when the Blank order
 *  box exists and is unticked). */
export function linesMissingLogo(
  lines: Array<{ id: string; itemName: string; decorationPath: string | null | undefined; decorations: PoPreconditionDecoration[] }>,
  blankOrder: boolean | null | undefined,
): MissingArtworkLocation[] {
  if (blankOrder !== false) return [];
  const locs: PoArtworkLocation[] = [];
  for (const l of lines) {
    if (!lineNeedsArtwork(l.decorationPath, blankOrder)) continue;
    if (l.decorations.length === 0) {
      locs.push({ lineItemId: l.id, itemName: l.itemName, location: null, hasLogoFile: false, hasLogoName: false, hasProofType: false });
      continue;
    }
    for (const d of l.decorations) {
      locs.push({ lineItemId: l.id, itemName: l.itemName, location: d.location, hasLogoFile: d.hasLogoFile, hasLogoName: d.hasLogoName, hasProofType: d.hasProofType });
    }
  }
  return locationsMissingArtwork(locs);
}

export interface PoPrecondition {
  /** true → the Create-POs action is enabled. */
  ok: boolean;
  /** The single (highest-priority) blocking reason. Undefined when ok. */
  reason?: PoBlockReason;
  /** Plain, rep-facing one-liner for the disabled state. Undefined when ok. */
  message?: string;
  /** Items still needing artwork — drives the "N items need artwork" copy. */
  artworkMissingCount: number;
  /** Only missing_artwork keeps a "Create anyway" override (skip_artwork_check);
   *  every other reason is a hard block until the rep fixes the data. */
  overridable: boolean;
  /** The first offending line's id (vendor_missing / the first missing-artwork
   *  pair) so the UI can deep-link the reason to that line's edit action.
   *  Undefined for the other reasons. */
  offendingLineId?: string;
  /** The missing (item, location) artwork pairs (missing_artwork only) — the UI
   *  deep-links each one to its offending line. Empty for every other reason. */
  missingArtwork: MissingArtworkLocation[];
}

/**
 * Derive the single blocking precondition (or ok) for creating POs from a Sales
 * Order. Reasons are checked in the SAME order the server enforces them so the
 * client surfaces the first thing the endpoint would reject:
 *   1. no_shipping_address  2. no_line_items  3. vendor_missing  4. missing_artwork
 *
 * missing_artwork is the only overridable reason (blanks / samples legitimately
 * ship without artwork — the server's skip_artwork_check escape hatch). Every
 * other reason is a real data gap the rep must close first.
 */
export function derivePoPreconditions(
  input: PoPreconditionInput,
): PoPrecondition {
  const base = {
    artworkMissingCount: 0,
    overridable: false,
    missingArtwork: [] as MissingArtworkLocation[],
  };

  // 1. Shipping address (server step 1).
  if (!input.hasShippingAddress) {
    return {
      ok: false,
      reason: "no_shipping_address",
      message: "Add a shipping address first.",
      ...base,
    };
  }
  // 2. At least one product line (server step 2 — services don't get POs).
  if (input.productLines.length === 0) {
    return {
      ok: false,
      reason: "no_line_items",
      message: "Add product lines first.",
      ...base,
    };
  }
  // 3. Every product line resolves a supplier (server step 3). The remedy is a
  // concrete, real action: open the offending line and re-select its product,
  // which re-snapshots the supplier (there is NO "re-sync" affordance in the
  // app — that copy named a button that doesn't exist). Surface the first
  // offending line so the UI can deep-link straight to its edit action.
  const noVendor = input.productLines.find((l) => !l.hasVendor);
  if (noVendor) {
    return {
      ok: false,
      reason: "vendor_missing",
      message:
        "A product is missing its supplier — edit the line and re-select the product to restore it.",
      offendingLineId: noVendor.id,
      ...base,
    };
  }
  // 4. Per-LOCATION artwork gate (server step 4, T15) — the overridable reason.
  // This runs the IDENTICAL decision the server runs (locationsMissingArtwork),
  // so the button and the endpoint can never drift into a client-ready /
  // server-409 divergence (the round-47 bug class). Every non-blank decoration
  // LOCATION needs a logo file + a logo name + a chosen proof type; a decorated
  // line with no locations yet is a gap too. logoColor is NOT gated. The message
  // is the SAME formatMissingArtworkLocations detail the server returns in
  // body.error, so the disabled-CTA copy and the 409 toast read identically.
  const artLocations: PoArtworkLocation[] = [];
  for (const l of input.productLines) {
    if (!l.needsArtwork) continue;
    if (l.decorations.length === 0) {
      artLocations.push({
        lineItemId: l.id,
        itemName: l.itemName,
        location: null,
        hasLogoFile: false,
        hasLogoName: false,
        hasProofType: false,
      });
      continue;
    }
    for (const d of l.decorations) {
      artLocations.push({
        lineItemId: l.id,
        itemName: l.itemName,
        location: d.location,
        hasLogoFile: d.hasLogoFile,
        hasLogoName: d.hasLogoName,
        hasProofType: d.hasProofType,
      });
    }
  }
  const missingArtwork = locationsMissingArtwork(artLocations);
  if (missingArtwork.length > 0) {
    return {
      ok: false,
      reason: "missing_artwork",
      message: formatMissingArtworkLocations(missingArtwork),
      artworkMissingCount: missingArtwork.length,
      // Not a blank order → the logo is required, no "Create anyway".
      overridable: input.blankOrder !== false,
      offendingLineId: missingArtwork[0]?.lineItemId,
      missingArtwork,
    };
  }

  // 5. Colors and sizes (Stephanie 2026-10-14) — server step 4a. No override.
  const noSizes = input.productLines.filter((l) => l.missingSizes === true);
  if (noSizes.length > 0) {
    return {
      ok: false,
      reason: "missing_sizes",
      message: `Add colors and sizes on the quote for: ${noSizes.map((l) => l.itemName).join(", ")}`,
      offendingLineId: noSizes[0]?.id,
      ...base,
    };
  }

  return { ok: true, ...base };
}

/**
 * Which decorated lines still lack artwork, given the two REAL artwork signals.
 *
 * Two signals must BOTH count as "has artwork" (code review 2026-07-26):
 *   - `artworkRowLineIds` — line_item_artwork rows, written ONLY by the legacy
 *     POST /orders/:id/artwork route (kept for back-compat);
 *   - `decoArtworkLineIds` — decorations carrying a logo file (logoFileId, set by
 *     OrderLogoUploader) or an attached artwork id. THIS is the mechanism the UI
 *     actually populates. Keying the server gate on line_item_artwork ALONE 409'd
 *     every decorated Quote on first PO create (no frontend writes that
 *     table) — the exact ping-pong this change kills. The client mirror already
 *     reads the logoFileId||artworkId signal, so honouring it server-side makes
 *     the two agree. (T15's per-location artwork gate later supersedes this.)
 *
 * Pure set math so the server derive AND the offline gate run the SAME decision.
 */
export function linesMissingArtwork(args: {
  needingArtworkLineIds: string[];
  artworkRowLineIds: string[];
  decoArtworkLineIds: string[];
}): string[] {
  const satisfied = new Set([
    ...args.artworkRowLineIds,
    ...args.decoArtworkLineIds,
  ]);
  return args.needingArtworkLineIds.filter((id) => !satisfied.has(id));
}

// ── Per-LOCATION artwork gate (SO→PO rebuild Round 2, T15) ──────────────────
//
// The per-line `linesMissingArtwork` gate above asks a coarse question ("does
// this decorated line carry ANY logo?"). T15 replaces the SERVER gate with a
// per-LOCATION one: on a real vendor PO every decoration LOCATION needs its own
// complete artwork spec, because the supplier decorates each location. A line
// with a Left-Chest logo but a bare Full-Back location must NOT mint a PO that
// tells the vendor "just do the back somehow".
//
// The three required fields are EXACTLY: a logo file, a logo name, and a
// structured proof type. logoColor is deliberately NOT gated (open CEO question
// 2026-07-27 — do not add it without a decision). Pure so the server derive AND
// the offline gate run the identical decision.

/** The gate field a location is missing — named for the (item, location) error. */
export type ArtworkGateField = "logo file" | "logo name" | "proof type";

/** One decoration LOCATION's artwork-completeness slice. The caller normalises
 *  a line_item_decorations row (or a decorated line with no rows) into this. */
export interface PoArtworkLocation {
  /** The order line id this location belongs to (scoping / grouping only). */
  lineItemId: string;
  /** Human item name (productSnapshot.name) — surfaced in the (item, location)
   *  pair so the rep knows which item to fix. Blank falls back to "Item". */
  itemName: string;
  /** The decoration's location label; null / blank → "Unnamed location". */
  location: string | null;
  /** line_item_decorations.logo_file_id is set (the uploaded artwork). */
  hasLogoFile: boolean;
  /** line_item_decorations.logo_name is set (non-blank). */
  hasLogoName: boolean;
  /** line_item_decorations.proof_type is chosen — ANY enum value counts,
   *  INCLUDING 'none' ("no proof required" is a real, explicit rep decision;
   *  null = the rep never chose). */
  hasProofType: boolean;
}

/** A location that failed the gate, named as an (item, location) pair plus the
 *  specific fields it still needs. */
export interface MissingArtworkLocation {
  lineItemId: string;
  itemName: string;
  location: string;
  missing: ArtworkGateField[];
}

/** Sentinel label for a decoration with no location text (or a decorated line
 *  carrying no decoration rows at all — still a real gap). */
export const UNNAMED_ARTWORK_LOCATION = "Unnamed location";

/**
 * Per-LOCATION artwork gate (T15). Given every decoration location on the lines
 * about to be minted, return the ones missing any of logo-file / logo-name /
 * proof-type — each as an (item, location) pair with the specific gaps. An empty
 * result ⇒ the gate passes. Order-preserving so the error reads in line order.
 */
export function locationsMissingArtwork(
  locations: PoArtworkLocation[],
): MissingArtworkLocation[] {
  const out: MissingArtworkLocation[] = [];
  for (const loc of locations) {
    const missing: ArtworkGateField[] = [];
    if (!loc.hasLogoFile) missing.push("logo file");
    if (!loc.hasLogoName) missing.push("logo name");
    if (!loc.hasProofType) missing.push("proof type");
    if (missing.length > 0) {
      out.push({
        lineItemId: loc.lineItemId,
        itemName: loc.itemName.trim() || "Item",
        location: loc.location?.trim() || UNNAMED_ARTWORK_LOCATION,
        missing,
      });
    }
  }
  return out;
}

/**
 * Format the missing (item, location) pairs BY NAME for a PoPrereqError detail,
 * e.g. `Tote — Left Chest (logo file, proof type); Cap — Front (logo name)`.
 * Truncates to `max` pairs with an ellipsis (mirrors the other prereq details).
 */
export function formatMissingArtworkLocations(
  missing: MissingArtworkLocation[],
  max = 3,
): string {
  const names = missing.map(
    (m) => `${m.itemName} — ${m.location} (${m.missing.join(", ")})`,
  );
  const shown = names.slice(0, max).join("; ");
  return names.length > max ? `${shown}…` : shown;
}

/** The per-line slice the zero-cost guard reads. */
export interface PoCostLine {
  name: string;
  /** The PO unit cost = the order line's net cost (string from the API, number
   *  in fixtures). Coerced; a non-finite value is treated as zero-cost. */
  unitCost: number | string | null | undefined;
  /** Only product lines can be a free-goods footgun; service lines (shipping /
   *  setup) are legitimately $0 and are excluded. */
  isProduct: boolean;
}

/**
 * Product-line names whose PO unit cost is ≤ 0 (or non-finite) — a "free goods"
 * PO that tells the supplier the items cost nothing. Mirrors
 * services/purchase-orders-builder.ts::listZeroCostProductLines and
 * listPoPricingWarnings' `zero_cost` rule so the client pre-create confirm and
 * the server backstop flag the exact same lines. Non-empty ⇒ refuse-then-confirm.
 */
export function zeroCostProductLineNames(lines: PoCostLine[]): string[] {
  return lines
    .filter((l) => l.isProduct)
    .filter((l) => {
      const n = typeof l.unitCost === "number" ? l.unitCost : Number(l.unitCost);
      return !Number.isFinite(n) || n <= 0;
    })
    .map((l) => l.name);
}

// ─── Send gate: ship method + shipping account (Stephanie 2026-10-14) ──────
// "Make it required that the PO contains ship method and shipping account
// number." The PO page disables Send with this list; POST /pos/:id/send
// refuses (409 shipping_details_missing) with the same words.

export type PoShippingDetail =
  | "ship-to address"
  | "ship method"
  | "shipping account"
  | "supplier email"
  | "ship-by date (before the in-hands date)";

/** "a", "a and b", "a, b and c" — for the "Add the … before sending" notes. */
export function joinMissing(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** The ship-by date has to be BEFORE the in-hands date — never the same day,
 *  never after (Stephanie 2026-10-14). Dates are YYYY-MM-DD, so plain string
 *  comparison is calendar order. No ship-by date = not OK; no in-hands date
 *  = nothing to compare against, so OK. */
export function shipDateBeforeInHands(
  shipBy: string | null | undefined,
  inHands: string | null | undefined,
): boolean {
  const s = (shipBy ?? "").slice(0, 10);
  const h = (inHands ?? "").slice(0, 10);
  if (!s) return false;
  if (!h) return true;
  return s < h;
}

/** What's missing before a PO can be sent — empty when it's ready.
 *  `accountRequired` is false until Other's own accounts and the "ship on
 *  supplier's account" choice exist (push-house-shipping-accounts.ts), so a
 *  PO shipping on Other's account is never stuck with nothing to pick. */
export function missingPoShippingDetails(
  po: {
    shippingMethod: string | null | undefined;
    shippingAccountId: string | null | undefined;
    requiredShipDate?: string | null;
  },
  opts: {
    accountRequired?: boolean;
    /** The supplier's order email on file; pass it to require one (Stephanie
     *  2026-10-14: "if there is no vendor email on file, it should be required
     *  to add one"). Omit to skip the check. */
    vendorEmail?: string | null;
    /** The PO's ship-to snapshot; pass it to require a street + city (pilot
     *  finding #2: a blank supplier's PO went out with only the decorator's
     *  name as the ship-to). Omit to skip the check. */
    shipTo?: unknown;
    /** The order's in-hands date; pass it to require a ship-by date that is
     *  before it (Stephanie 2026-10-14). Omit to skip the check. */
    inHandsDate?: string | null;
    /** The PO ships on the supplier's own account — then the supplier picks
     *  the method, so none is required (Stephanie 2026-10-14). */
    shipsOnSupplierAccount?: boolean;
  } = {},
): PoShippingDetail[] {
  const missing: PoShippingDetail[] = [];
  if ("shipTo" in opts) {
    const a = (opts.shipTo ?? {}) as Record<string, unknown>;
    const str = (k: string) => (typeof a[k] === "string" ? (a[k] as string).trim() : "");
    if (!(str("street1") || str("line1")) || !str("city")) missing.push("ship-to address");
  }
  if ("vendorEmail" in opts && !(opts.vendorEmail ?? "").trim()) missing.push("supplier email");
  if (!opts.shipsOnSupplierAccount && (!po.shippingMethod || po.shippingMethod.trim() === "")) missing.push("ship method");
  if ((opts.accountRequired ?? true) && !po.shippingAccountId) missing.push("shipping account");
  if ("inHandsDate" in opts && !shipDateBeforeInHands(po.requiredShipDate, opts.inHandsDate)) {
    missing.push("ship-by date (before the in-hands date)");
  }
  return missing;
}
